import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { MatDialog as MatDialog } from "@angular/material/dialog";
import { MatPaginator as MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource as MatTableDataSource } from "@angular/material/table";
import { Store } from "@ngrx/store";
import { finalize, map } from "rxjs/operators";
import { EaredDoveService } from "../../eared-dove/eared-dove.service";
import {
  TokenFrequencyWithPos,
  TokenWithLexiconStatus,
} from "../models/models.model";
import { EditLexiconDialogComponent } from "../../lexicon-sentiment-tuning/edit-lexicon-dialog/edit-lexicon-dialog.component";
import {
  LexiconRow,
  LexiconTab,
  LexiconTable,
} from "../../lexicon-sentiment-tuning/model";
import {
  AddNewLexicon,
  UpdateLexiconRecord,
} from "../../lexicon-sentiment-tuning/state/lexicon-sentiment-tuning.actions";

@Component({
  selector: "app-token-frequency-table",
  templateUrl: "./token-frequency-table.component.html",
  styleUrls: ["./token-frequency-table.component.scss"],
  standalone: false,
})
export class TokenFrequencyTableComponent
  implements OnInit, AfterViewInit, OnChanges
{
  @Input() workspace: string;
  @Input() dataSourceId: string;
  @Input() lexiconTable: LexiconTable;
  @Input() lexiconTab: LexiconTab;
  @Input() configId: string;
  @Input() readOnly: boolean;
  @Input() isLoading: boolean;

  @Output() searchToken: EventEmitter<string> = new EventEmitter();

  @ViewChild(MatSort) tokenSort: MatSort;
  @ViewChild(MatPaginator) tokenPaginator: MatPaginator;

  // Token frequency properties
  tokenFrequencyData = new MatTableDataSource<TokenWithLexiconStatus>([]);
  tokenDisplayedColumns: string[] = [
    "token",
    "frequency",
    "token_pos",
    "existsInLexicon",
    "actions",
  ];
  tokenLoading = false;
  // Filter: show only tokens that exist in lexicon
  showOnlyInLexicon = false;
  // Free-text token search
  tokenSearch = "";

  constructor(
    public store: Store<any>,
    public dialog: MatDialog,
    private earedDoveService: EaredDoveService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Cargar datos automáticamente si workspace y dataSourceId están disponibles
    if (this.workspace && this.dataSourceId) {
      this.loadTokenFrequency();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Recargar datos si workspace o dataSourceId cambian
    if (
      (changes["workspace"] || changes["dataSourceId"]) &&
      this.workspace &&
      this.dataSourceId
    ) {
      this.loadTokenFrequency();
    }
  }

  ngAfterViewInit(): void {
    this.configureTableFeatures();
    // initialize filter predicate
    // Combined predicate: accepts either a plain 'true'/'false' string for lexicon toggle
    // or a JSON payload { onlyInLexicon: 'true'|'false', text: '<search>' }
    this.tokenFrequencyData.filterPredicate = (
      data: TokenWithLexiconStatus,
      filter: string,
    ) => {
      let onlyIn = false;
      let text = "";
      try {
        const parsed = JSON.parse(filter);
        onlyIn = parsed && parsed.onlyInLexicon === "true";
        text = parsed && parsed.text ? parsed.text + "" : "";
      } catch (e) {
        // if not JSON, legacy behavior: onlyInLexicon filter
        onlyIn = filter === "true";
      }

      if (onlyIn && !data.existsInLexicon) {
        return false;
      }

      if (!text || text.trim() === "") {
        return true;
      }

      const hay = (
        (data.token || "") +
        " " +
        (data.token_pos || "") +
        " " +
        (data.frequency || "") +
        " " +
        (data.lexiconValue !== undefined ? data.lexiconValue : "")
      ).toLowerCase();
      return hay.indexOf(text.trim().toLowerCase()) !== -1;
    };
  }

  private configureTableFeatures(): void {
    // Usar setTimeout para asegurar que los ViewChild estén disponibles
    setTimeout(() => {
      if (this.tokenSort) {
        this.tokenFrequencyData.sort = this.tokenSort;
      }
      if (this.tokenPaginator) {
        this.tokenFrequencyData.paginator = this.tokenPaginator;
      }
      // Detect changes only if needed after setting the paginator or sort
      if (this.tokenSort || this.tokenPaginator) {
        this.cdr.detectChanges();
      }
    });
  }

  // Token frequency methods
  loadTokenFrequency() {
    if (!this.workspace || !this.dataSourceId) {
      console.warn(
        "Workspace or dataSourceId not provided for token frequency",
      );
      return;
    }

    this.tokenLoading = true;
    this.earedDoveService
      .getTokenFrequencyWithPos(this.workspace, this.dataSourceId)
      .pipe(map((tokens) => this.enrichTokensWithLexiconStatus(tokens)))
      .pipe(finalize(() => (this.tokenLoading = false)))
      .subscribe({
        next: (enrichedTokens) => {
          this.tokenFrequencyData.data = enrichedTokens;
          // Apply current filter after loading data
          this.applyLexiconFilter();
          this.configureTableFeatures(); // Reconfigure table features after data load
        },
        error: (error) => {
          console.error("Error loading token frequency:", error);
        },
      });
  }

  applyLexiconFilter() {
    // Use MatTableDataSource.filter to trigger the predicate.
    // Combine lexicon toggle + free-text search into a JSON payload
    const payload = {
      onlyInLexicon: this.showOnlyInLexicon ? "true" : "false",
      text: this.tokenSearch || "",
    };
    this.tokenFrequencyData.filter = JSON.stringify(payload);
    // If paginator is present, reset to first page to avoid empty pages
    if (this.tokenFrequencyData.paginator) {
      this.tokenFrequencyData.paginator.firstPage();
    }
  }

  onSearchChange(value: string) {
    this.tokenSearch = value || "";
    this.applyLexiconFilter();
  }

  private enrichTokensWithLexiconStatus(
    tokens: TokenFrequencyWithPos[],
  ): TokenWithLexiconStatus[] {
    return tokens.map((token) => {
      const existsInLexicon = this.checkIfTokenExistsInLexicon(token.token);
      const lexiconValue = existsInLexicon
        ? this.getLexiconValue(token.token)
        : undefined;

      return {
        ...token,
        existsInLexicon,
        lexiconValue,
      };
    });
  }

  private checkIfTokenExistsInLexicon(token: string): boolean {
    return this.lexiconTable.rows.data.some(
      (row) => row.label.toLowerCase() === token.toLowerCase(),
    );
  }

  private getLexiconValue(token: string): number | undefined {
    const lexiconRow = this.lexiconTable.rows.data.find(
      (row) => row.label.toLowerCase() === token.toLowerCase(),
    );
    const value = Number(lexiconRow?.value);
    return !isNaN(value) ? value : undefined;
  }

  addTokenToLexicon(token: TokenWithLexiconStatus) {
    if (this.readOnly) {
      return;
    }

    const config = {
      data: {
        title: "Add Token to Lexicon",
        type: this.lexiconTable.type,
        valueString: this.lexiconTable.valueString,
        originalWord: token.token,
        originalValue: 0,
        onConfirm: (word, value) => {
          this.store.dispatch(
            new AddNewLexicon(this.configId, this.lexiconTab, word, value),
          );
          // Refresh token table after adding
          setTimeout(() => {
            this.loadTokenFrequency();
          }, 1000);
        },
      },
    };
    this.dialog.open(EditLexiconDialogComponent, config);
  }

  editTokenInLexicon(token: TokenWithLexiconStatus) {
    if (this.readOnly || !token.existsInLexicon) {
      return;
    }

    const config = {
      data: {
        title: "Edit Token in Lexicon",
        type: this.lexiconTable.type,
        valueString: this.lexiconTable.valueString,
        originalWord: token.token,
        originalValue: token.lexiconValue,
        onConfirm: (word, value) => {
          this.store.dispatch(
            new UpdateLexiconRecord(
              this.configId,
              this.lexiconTab,
              word,
              value,
              token.token,
            ),
          );
          // Refresh token table after editing
          setTimeout(() => {
            this.loadTokenFrequency();
          }, 1000);
        },
      },
    };
    this.dialog.open(EditLexiconDialogComponent, config);
  }

  searchInSentences(token: string) {
    this.searchToken.emit(token);
  }
}
